import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { HrDepartment, HrPosition, HrStructure } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import './hr.css';

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);

/** Gemeinsame Abfrage für diese Seite und die Auswahlfelder im Mitarbeiterformular */
export function useHrStructure(enabled = true) {
  return useQuery({ queryKey: ['hr-structure'], queryFn: async () => (await api<{ data: HrStructure }>('GET', '/hr-structure')).data, enabled });
}

/**
 * Personalmanagement → Abteilungen & Positionen (KON-236). Hier legt der Mandant die Struktur seines Unternehmens fest. Die Positionen stehen danach als Auswahl im Mitarbeiterformular
 * und in der Schichtplanung. Beim Umbenennen ziehen die Mitarbeiter mit, gelöscht wird nur, was niemand mehr verwendet. Die Seite braucht `hr.read`, ändern darf, wer `hr.write` hat.
 */
export function StructurePage() {
  const { can } = useSession();
  const client = useQueryClient();
  const canWrite = can('hr.write');
  const structure = useHrStructure();
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [newDepartment, setNewDepartment] = useState('');
  const [newPosition, setNewPosition] = useState('');
  const [newPositionDepartment, setNewPositionDepartment] = useState('');
  const [editing, setEditing] = useState<{ kind: 'department' | 'position'; id: string; name: string; departmentId: string } | null>(null);

  const done = (okText: string) => (res: { data: HrStructure }) => {
    client.setQueryData(['hr-structure'], res.data);
    client.invalidateQueries({ queryKey: ['shift-settings'] });
    client.invalidateQueries({ queryKey: ['employees'] });
    setMessage({ ok: true, text: okText });
  };
  const failed = (fallback: string) => (e: unknown) => setMessage({ ok: false, text: errorText(e, fallback) });

  const addDepartment = useMutation({
    mutationFn: (name: string) => api<{ data: HrStructure }>('POST', '/hr-structure/departments', { name }),
    onSuccess: (res) => { done('Die Abteilung ist angelegt.')(res); setNewDepartment(''); },
    onError: failed('Die Abteilung konnte nicht angelegt werden.'),
  });
  const addPosition = useMutation({
    mutationFn: (v: { name: string; departmentId: string | null }) => api<{ data: HrStructure }>('POST', '/hr-structure/positions', v),
    onSuccess: (res) => { done('Die Position ist angelegt.')(res); setNewPosition(''); },
    onError: failed('Die Position konnte nicht angelegt werden.'),
  });
  const saveEdit = useMutation({
    mutationFn: (e: NonNullable<typeof editing>) =>
      e.kind === 'department'
        ? api<{ data: HrStructure }>('PATCH', `/hr-structure/departments/${e.id}`, { name: e.name })
        : api<{ data: HrStructure }>('PATCH', `/hr-structure/positions/${e.id}`, { name: e.name, departmentId: e.departmentId || null }),
    onSuccess: (res) => { done('Die Änderung ist gespeichert. Mitarbeiter und Schichtplanung sind angepasst.')(res); setEditing(null); },
    onError: failed('Das Speichern hat nicht geklappt.'),
  });
  const remove = useMutation({
    mutationFn: (v: { kind: 'department' | 'position'; id: string }) => api<{ data: HrStructure }>('DELETE', `/hr-structure/${v.kind === 'department' ? 'departments' : 'positions'}/${v.id}`),
    onSuccess: done('Der Eintrag ist gelöscht.'),
    onError: failed('Das Löschen hat nicht geklappt.'),
  });

  const data = structure.data;
  if (structure.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!data) return <div className="alert alert--error" role="alert">Die Struktur konnte nicht geladen werden.</div>;

  const submitDepartment = (e: FormEvent) => { e.preventDefault(); setMessage(null); if (newDepartment.trim()) addDepartment.mutate(newDepartment.trim()); };
  const submitPosition = (e: FormEvent) => { e.preventDefault(); setMessage(null); if (newPosition.trim()) addPosition.mutate({ name: newPosition.trim(), departmentId: newPositionDepartment || null }); };
  const startEdit = (kind: 'department' | 'position', row: HrDepartment | HrPosition) => {
    setMessage(null);
    setEditing({ kind, id: row.id, name: row.name, departmentId: kind === 'position' ? ((row as HrPosition).departmentId ?? '') : '' });
  };
  const confirmDelete = (kind: 'department' | 'position', row: HrDepartment | HrPosition) => {
    setMessage(null);
    if (window.confirm(`${kind === 'department' ? 'Die Abteilung' : 'Die Position'} „${row.name}“ löschen?`)) remove.mutate({ kind, id: row.id });
  };
  const editForm = (kind: 'department' | 'position') =>
    editing && editing.kind === kind ? (
      <form className="row-actions" onSubmit={(e) => { e.preventDefault(); setMessage(null); saveEdit.mutate(editing); }}>
        <input className="input" aria-label="Name" value={editing.name} maxLength={100} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
        {kind === 'position' && (
          <select className="input" aria-label="Abteilung" value={editing.departmentId} onChange={(e) => setEditing({ ...editing, departmentId: e.target.value })}>
            <option value="">ohne Abteilung</option>
            {data.departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        )}
        <button className="btn btn--primary btn--sm" type="submit" disabled={saveEdit.isPending}>Speichern</button>
        <button className="btn btn--secondary btn--sm" type="button" onClick={() => setEditing(null)}>Abbrechen</button>
      </form>
    ) : null;

  return (
    <>
      <div className="page-head">
        <h1>Abteilungen &amp; Positionen</h1>
      </div>
      <p className="muted">
        Hier legen Sie die Struktur Ihres Unternehmens fest. Die Positionen stehen danach als Auswahl beim Anlegen eines Mitarbeiters und in der Schichtplanung zur Verfügung. Benennen Sie
        etwas um, passt Wirkia die Mitarbeiter und die Sollstunden der Schichtplanung mit an. Löschen lässt sich nur, was kein Mitarbeiter mehr hat.
      </p>
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}

      <section className="card">
        <h2>Abteilungen</h2>
        {data.departments.length === 0 ? (
          <p className="muted">Noch keine Abteilung angelegt. Beispiele: Service, Küche, Verwaltung, Fahrdienst.</p>
        ) : (
          <table className="table table--cards">
            <thead><tr><th>Abteilung</th><th>Positionen</th><th>Mitarbeiter</th>{canWrite && <th><span className="sr-only">Aktionen</span></th>}</tr></thead>
            <tbody>
              {data.departments.map((d) => (
                <tr key={d.id}>
                  <td data-label="Abteilung">{editing?.kind === 'department' && editing.id === d.id ? editForm('department') : <strong>{d.name}</strong>}</td>
                  <td data-label="Positionen">{d.positions}</td>
                  <td data-label="Mitarbeiter">{d.employees}</td>
                  {canWrite && (
                    <td data-label="Aktionen">
                      <div className="row-actions">
                        <button className="btn btn--secondary btn--sm" type="button" onClick={() => startEdit('department', d)}>Umbenennen</button>
                        <button className="btn btn--secondary btn--sm" type="button" disabled={remove.isPending} onClick={() => confirmDelete('department', d)}>Löschen</button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {canWrite && (
          <form className="structure-add" onSubmit={submitDepartment}>
            <label className="field">
              <span>Neue Abteilung</span>
              <input className="input" value={newDepartment} maxLength={100} onChange={(e) => setNewDepartment(e.target.value)} autoComplete="off" />
            </label>
            <button className="btn btn--primary" type="submit" disabled={addDepartment.isPending || !newDepartment.trim()}>Abteilung anlegen</button>
          </form>
        )}
      </section>

      <section className="card">
        <h2>Positionen</h2>
        {data.positions.length === 0 ? (
          <p className="muted">Noch keine Position angelegt. Beispiele: Kellner, Koch, Fahrer, Disponent.</p>
        ) : (
          <table className="table table--cards">
            <thead><tr><th>Position</th><th>Abteilung</th><th>Mitarbeiter</th>{canWrite && <th><span className="sr-only">Aktionen</span></th>}</tr></thead>
            <tbody>
              {data.positions.map((p) => (
                <tr key={p.id}>
                  <td data-label="Position" colSpan={editing?.kind === 'position' && editing.id === p.id ? 2 : 1}>{editing?.kind === 'position' && editing.id === p.id ? editForm('position') : <strong>{p.name}</strong>}</td>
                  {!(editing?.kind === 'position' && editing.id === p.id) && <td data-label="Abteilung">{p.departmentName ?? <span className="muted">ohne Abteilung</span>}</td>}
                  <td data-label="Mitarbeiter">{p.employees}</td>
                  {canWrite && (
                    <td data-label="Aktionen">
                      <div className="row-actions">
                        <button className="btn btn--secondary btn--sm" type="button" onClick={() => startEdit('position', p)}>Bearbeiten</button>
                        <button className="btn btn--secondary btn--sm" type="button" disabled={remove.isPending} onClick={() => confirmDelete('position', p)}>Löschen</button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {canWrite && (
          <form className="structure-add" onSubmit={submitPosition}>
            <label className="field">
              <span>Neue Position</span>
              <input className="input" value={newPosition} maxLength={100} onChange={(e) => setNewPosition(e.target.value)} autoComplete="off" />
            </label>
            <label className="field">
              <span>Abteilung</span>
              <select className="input" value={newPositionDepartment} onChange={(e) => setNewPositionDepartment(e.target.value)}>
                <option value="">ohne Abteilung</option>
                {data.departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </label>
            <button className="btn btn--primary" type="submit" disabled={addPosition.isPending || !newPosition.trim()}>Position anlegen</button>
          </form>
        )}
        <p className="muted small">
          Sollstunden je Position stellen Sie in der Einrichtung unter <Link to="/einrichtung/schichtplanung">Schichtplanung</Link> ein.
        </p>
      </section>
    </>
  );
}
